import { CSSProperties, FC, KeyboardEvent, ReactNode, useCallback, useMemo } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { classificaDensita, densitaDi } from '@leopart/calc';
import { rigaDensita, rigaMallo } from '../allenamenti.logica';
import { ITrainingBlock, RESPONSIBLE_OPTIONS } from '../../../models/allenamenti';
import { FALLBACK_CATEGORY_COLOR } from '../allenamenti.constants';
import BlockCardMenu from './BlockCardMenu';
import styles from './BlockCard.module.scss';

interface IBlockCardBodyProps {
  block: ITrainingBlock;
  index: number;
  /**
   * Le categorie del blocco, ognuna col suo colore. Sono più d'una da quando
   * un blocco può appartenere a categorie diverse.
   */
  categories: { label: string; color?: string }[];
  subcategoryLabels?: string[];
  /**
   * Contenuto extra nell'angolo in alto a destra della riga `.top` (oggi: il
   * kebab "Copia" di BlockCard, F1). Assente di proposito nell'anteprima del
   * DragOverlay in WeekBoard, che non è interattiva.
   */
  topRightSlot?: ReactNode;
}

/**
 * Contenuto visivo di una card: condiviso fra la card interattiva qui sotto
 * e l'anteprima che WeekBoard mostra nel DragOverlay durante il trascinamento
 * (stesso markup, contesto diverso — lì non è né bottone né draggable).
 */
export const BlockCardBody: FC<IBlockCardBodyProps> = ({
  block,
  index,
  categories,
  subcategoryLabels,
  topRightSlot,
}) => {
  const responsible = RESPONSIBLE_OPTIONS.find((r) => r.value === block.responsible);

  const densityLine = rigaDensita(block);
  const malloLine = rigaMallo(block);
  const densita = densitaDi(block.fieldWidthM, block.fieldLengthM, block.playersOnField);
  const densityRange = densita == null ? null : classificaDensita(densita);

  return (
    <>
      <span className={styles.top}>
        <span className={styles.num}>#{index + 1}</span>
        {/* Ogni categoria col proprio colore: con due o tre nomi affiancati,
            tingerli tutti dello stesso accento renderebbe impossibile capire
            quale colore corrisponde a quale. */}
        <span className={styles.categories}>
          {categories.map((c, i) => (
            <span key={c.label}>
              {i > 0 && <span className={styles.sep}> · </span>}
              <span style={{ color: c.color ?? FALLBACK_CATEGORY_COLOR }}>
                {c.label}
              </span>
            </span>
          ))}
        </span>
        <span className={styles.duration}>{block.durationMinutes}&apos;</span>
        {topRightSlot}
      </span>

      <span className={styles.title}>{block.title}</span>
      {!!subcategoryLabels?.length && (
        <span className={styles.sub}>{subcategoryLabels.join(' · ')}</span>
      )}
      {/* Contesto di gioco (ADR 029): righe compatte formattate in
          allenamenti.logica (stessa fonte di BlocksTable), densità mai
          persistita. Il range colorato è l'accento, come per le categorie. */}
      {densityLine && (
        <span className={styles.fieldLine}>
          {densityLine}
          {densityRange && (
            <>
              {' · '}
              {/* Colore del range: valore runtime dalle costanti calc. */}
              <b style={{ color: densityRange.color }}>{densityRange.labelIt}</b>
            </>
          )}
        </span>
      )}
      {malloLine && <span className={styles.fieldLine}>{malloLine}</span>}
      {/*
        Qui NON si usa `shared/CustomBadge` (rende un `<div>`): resta uno
        `<span>` con lo stile del modulo, coerente col resto della riga
        (num/categorie/durata sono anch'essi `<span>`) — sostituirlo non è
        oggetto di questa modifica.
      */}
      {responsible && <span className={styles.badge}>{responsible.label}</span>}
    </>
  );
};

/**
 * `topRightSlot` non è tra le prop accettate qui: BlockCard calcola sempre da
 * sé il proprio kebab (BlockCardMenu) — nessun chiamante deve poterlo
 * sovrascrivere.
 */
interface IBlockCardProps extends Omit<IBlockCardBodyProps, 'topRightSlot'> {
  onClick: (block: ITrainingBlock) => void;
  /**
   * Registra/deregistra il nodo DOM della card presso useWeekBoardDnd, che lo
   * usa per ridare il fuoco alla card dopo un drop da tastiera, quando è
   * rimontata in una colonna diversa (vedi useWeekBoardDnd.registerCardNode).
   */
  registerCardNode: (id: string, node: HTMLDivElement | null) => void;
}

/**
 * Un blocco nella colonna del giorno. Il colore della categoria è un accento
 * (bordo sinistro + etichetta) — unica eccezione allo stile inline, perché è
 * dato che arriva dall'API, non un token tema.
 *
 * È anche trascinabile: gli stessi listener che gestiscono il trascinamento
 * stanno sul contenitore che apre la modal. Con PointerSensor ad attivazione
 * a distanza, un click che non supera la soglia non fa mai partire un drag —
 * l'evento click nativo arriva comunque a onClick (stesso pattern di
 * CoachSessionWeekView/WeekStrip). Durante il drag la card resta al suo
 * posto, appena sbiadita: chi segue il trascinamento è il DragOverlay.
 *
 * `useSortable` e non `useDraggable`: serve che la card sia anche una zona di
 * rilascio, perché è lasciandola cadere SOPRA un'altra card che si sceglie la
 * posizione. Con il solo draggable si poteva mirare alla colonna e basta.
 *
 * `<div role="button" tabIndex={0}>` e non più un `<button>` (F1): serve a
 * ospitare il kebab "Copia" (BlockCardMenu, un vero `<button>` Chakra) senza
 * annidare un bottone dentro un altro bottone, content model non valido —
 * niente che riguardi la validità HTML è cambiato per il resto, il contenuto
 * di BlockCardBody restava già tutto in `<span>`.
 */
const BlockCard: FC<IBlockCardProps> = ({
  block,
  index,
  categories,
  subcategoryLabels,
  onClick,
  registerCardNode,
}) => {
  const accent = categories[0]?.color ?? FALLBACK_CATEGORY_COLOR;
  // Niente `transform` qui: chi segue il cursore è il DragOverlay in
  // WeekBoard (per non restare tagliato dall'overflow della colonna). Se
  // spostassimo anche questa card col transform di dnd-kit, la card
  // originale e l'overlay si muoverebbero insieme, sdoppiati.
  const { attributes, listeners, setNodeRef, isDragging } = useSortable({
    id: block._id,
  });
  // `listeners` è `undefined` finché nessun sensore è attivo: va composto,
  // non spalmato direttamente, perché ora `onKeyDown` deve anche aprire la
  // modal con Invio (vedi handleKeyDown sotto) — un `<button>` nativo lo
  // faceva da solo, un `<div>` no. `useMemo` (non un `?? {}` diretto): senza,
  // l'oggetto vuoto di fallback sarebbe nuovo a ogni render e farebbe
  // ricreare `handleKeyDown` inutilmente ad ogni render.
  const dndListeners = useMemo(() => listeners ?? {}, [listeners]);

  // Combina il ref di dnd-kit con la registrazione per il ripristino del
  // fuoco: due sistemi indipendenti che hanno entrambi bisogno dello stesso
  // nodo DOM.
  const setRefs = useCallback(
    (node: HTMLDivElement | null) => {
      setNodeRef(node);
      registerCardNode(block._id, node);
    },
    [setNodeRef, registerCardNode, block._id],
  );

  /**
   * Invio apre la modal, Spazio afferra da tastiera (useWeekBoardDnd) — la
   * stessa divisione che il `<button>` nativo otteneva gratis (Invio → click
   * su keydown, Spazio → click su keyup, intercettato da dnd-kit prima che
   * scatti). Qui va scritta a mano: prima lasciamo che dnd-kit gestisca lo
   * Spazio (fa lui `preventDefault` se lo intercetta), poi — solo se non l'ha
   * già consumato — gestiamo Invio.
   */
  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      dndListeners.onKeyDown?.(event);
      if (event.defaultPrevented) return;
      if (event.key === 'Enter') {
        event.preventDefault();
        onClick(block);
      }
    },
    [dndListeners, onClick, block],
  );

  // Due eccezioni allo stile-sempre-in-scss: l'accento di categoria (dato
  // API, statico) e l'opacità di trascinamento (stato runtime di dnd-kit).
  const style: CSSProperties = {
    borderLeftColor: accent,
    opacity: isDragging ? 0.35 : undefined,
  };

  return (
    <div
      ref={setRefs}
      className={styles.card}
      style={style}
      {...dndListeners}
      {...attributes}
      role="button"
      tabIndex={0}
      onClick={() => onClick(block)}
      onKeyDown={handleKeyDown}
    >
      <BlockCardBody
        block={block}
        index={index}
        categories={categories}
        subcategoryLabels={subcategoryLabels}
        topRightSlot={<BlockCardMenu block={block} />}
      />
    </div>
  );
};

export default BlockCard;
